import React, { useState, useEffect } from "react";

import { useSelector } from "react-redux";
import { dynamicSort, formatNumber } from "../../../../utils/MainHelper";
import RenderComponent from "../../../General/RenderComponent";
import ApexChart from "../../../Charts/ApexChart";

function WhiteSpaceColumnChart({
    visualData,
    clickBar = () => { },
    timePerChart
}) {
    const [isLoading, setIsLoading] = useState(true);
    const [notData, setNotData] = useState(false);
    const { whitespaceSegmentSizes } = useSelector((state) => state.reports);
    const newData = [...visualData].sort(dynamicSort("-diff", true)).map((d) => ({
        ...d,
        score: d.diff < 0 ? 0 : d.diff
    })).splice(0, 10);
    const maxPercent1 = Math.max(...newData.map((d) => d.brandSpace));
    const maxValue1 = (maxPercent1 > 100 ? maxPercent1 : 100) + 20;

    const maxPercent2 = Math.max(...newData.map((d) => d.score));
    const maxValue2 = maxPercent2 + 20;

    const newCategories = newData.reduce((acc, catObj) => {
        const cat = catObj.name;
        const catList = (`${cat} (${formatNumber(whitespaceSegmentSizes[catObj.path])})`).split(" ");
        if (catList.length === 1) {
            acc.push(cat);
        } else {
            let tempText = "";
            acc.push(catList.reduce((acc2, c, index) => {
                if ((`${tempText}${c}`).length >= 18) {
                    acc2.push(tempText.trim());
                    tempText = `${c} `;
                } else {
                    tempText += `${c} `;
                }
                if (index === catList.length - 1) { acc2.push(tempText.trim()); }
                return acc2;
            }, []));
        }
        return acc;
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, timePerChart);

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [timePerChart]);

    const setNoData = () => {
        setNotData(true);
    };

    return (
        <div style={{ width: "100%", height: "calc(100% - 9mm)" }} className="d-flex-col">
            <RenderComponent
                show={notData}
                isFullscreen
                loadingProps={{
                    loading: isLoading,
                    description: "Loading Chart. Please wait..."
                }}
            >
                <div style={{
                    width: "100%", height: "50%", padding: "2mm 0"
                }}
                >
                    <ApexChart
                        width="99%"
                        options={{
                            chart: {
                                toolbar: {
                                    show: false
                                },
                                animations: {
                                    enabled: false
                                },
                                id: "orig-column",
                                events: {
                                    click(event, chartContext, config) {
                                        // console.log(event, chartContext, config);
                                        const name = config.config.xaxis.categories[config.dataPointIndex];
                                        const score = config.config.series[0].data[config.dataPointIndex];
                                        if (name) {
                                            clickBar(name.join(" "), score);
                                        }
                                    },
                                    mounted: (e, e1) => {
                                        if (!e1.config.xaxis.categories.length) {
                                            setNoData(0);
                                        }
                                    }
                                }
                            },
                            grid: {
                                show: true,
                                borderColor: "#90A4AE",
                                strokeDashArray: 0,
                                position: "back",
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                row: {
                                    colors: undefined,
                                    opacity: 0.5
                                },
                                column: {
                                    colors: undefined,
                                    opacity: 0.5
                                },
                                padding: {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0
                                }
                            },
                            legend: {
                                show: false
                            },
                            xaxis: {
                                max: maxValue1,
                                categories: newCategories,
                                labels: {
                                    show: false,
                                    rotate: 0,
                                    style: {
                                        fontWeight: "bold"
                                    }
                                }
                            },
                            yaxis: {
                                max: maxValue1,
                                labels: {
                                    show: false,
                                    formatter(val) {
                                        return `${(val).toFixed(2)}%`;
                                    }
                                }

                            },
                            colors: ["#263668d9"],
                            plotOptions: {
                                bar: {
                                    dataLabels: {
                                        hideOverflowingLabels: false,
                                        position: "top"
                                    }
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                offsetY: -20,
                                style: {
                                    fontSize: "12px"
                                },
                                formatter: (val) => (val ? `${val}%` : ""),
                                background: {
                                    enabled: true,
                                    foreColor: "black",
                                    padding: 4,
                                    borderRadius: 0,
                                    borderWidth: 1,
                                    borderColor: "white",
                                    opacity: 0.9
                                }
                            },
                            annotations: {
                                position: "front",
                                points: [{
                                    x: 0,
                                    y: maxValue1,
                                    yAxisIndex: 0,
                                    seriesIndex: 0,
                                    marker: {
                                        size: 0
                                    },
                                    label: {
                                        offsetX: 60,
                                        borderColor: "#263668",
                                        borderWidth: 2,
                                        borderRadius: 0,
                                        text: "Brand Space",
                                        textAnchor: "middle",
                                        style: {
                                            background: "#263668",
                                            color: "white",
                                            fontSize: "12px",
                                            padding: {
                                                left: 10,
                                                right: 10,
                                                top: 5,
                                                bottom: 5
                                            }
                                        }
                                    }
                                }],
                                yaxis: [{
                                    y: 100,
                                    y2: null,
                                    strokeDashArray: 2,
                                    borderWidth: 2,
                                    borderColor: "gray",
                                    fillColor: "transparent",
                                    opacity: 0.3,
                                    width: "100%"
                                }]
                            }
                        }}
                        series={[{
                            name: "",
                            data: newData.map((d) => (d.brandSpace).toFixed(2))
                        }]}
                    />
                </div>
                <div style={{
                    position: "relative",
                    width: "100%",
                    height: "50%",
                    padding: "2mm 0"
                }}
                >
                    <ApexChart
                        width="99%"
                        options={{
                            chart: {
                                id: "orig-column",
                                offsetX: 0,
                                toolbar: {
                                    show: false
                                },
                                animations: {
                                    enabled: false
                                },
                                events: {
                                    click(event, chartContext, config) {
                                        // console.log(event, chartContext, config);
                                        const name = config.config.xaxis.categories[config.dataPointIndex];
                                        const score = config.config.series[0].data[config.dataPointIndex];
                                        if (name) {
                                            clickBar(name.join(" "), score, true);
                                        }
                                    },
                                    mounted: (e, e1) => {
                                        if (!e1.config.xaxis.categories.length) {
                                            setNoData(0);
                                        }
                                    }
                                }
                            },
                            grid: {
                                show: true,
                                borderColor: "#90A4AE",
                                strokeDashArray: 0,
                                position: "back",
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                row: {
                                    colors: undefined,
                                    opacity: 0.5
                                },
                                column: {
                                    colors: undefined,
                                    opacity: 0.5
                                },
                                padding: {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0
                                }
                            },
                            xaxis: {
                                max: maxValue2,
                                categories: newCategories,
                                labels: {
                                    rotate: 0,
                                    style: {
                                        fontWeight: "bold"
                                    }
                                }
                            },
                            yaxis: {
                                max: maxValue2,
                                labels: {
                                    show: false,
                                    formatter(val) {
                                        return `${val}%`;
                                    }
                                }

                            },
                            colors: ["#263668d9"],
                            plotOptions: {
                                bar: {
                                    colors: {
                                        backgroundBarColors: [],
                                        backgroundBarOpacity: 1,
                                        backgroundBarRadius: 0
                                    },
                                    dataLabels: {
                                        position: "top"
                                    }
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                offsetY: -20,
                                style: {
                                    fontSize: "12px",
                                    colors: ["#263668d9"]
                                },
                                formatter: (val) => `${val.toFixed(2)}%`
                            },
                            stroke: {
                                show: true,
                                curve: "smooth",
                                lineCap: "butt",
                                colors: ["#263668"],
                                width: 1,
                                dashArray: 0
                            },
                            fill: {
                                colors: "#263668",
                                opacity: 0.9,
                                type: "pattern",
                                pattern: {
                                    style: "slantedLines",
                                    width: 8,
                                    height: 8,
                                    strokeWidth: 2
                                }
                            },
                            annotations: {
                                position: "front",
                                points: [{
                                    x: 0,
                                    y: maxValue2,
                                    yAxisIndex: 0,
                                    seriesIndex: 0,
                                    marker: {
                                        size: 0
                                    },
                                    label: {
                                        offsetX: 60,
                                        borderColor: "#263668",
                                        borderWidth: 1,
                                        borderRadius: 0,
                                        text: "Market Gap",
                                        textAnchor: "middle",
                                        style: {
                                            background: "rgb(64 89 167)",
                                            color: "white",
                                            fontSize: "12px",
                                            padding: {
                                                left: 10,
                                                right: 10,
                                                top: 5,
                                                bottom: 5
                                            }
                                        }
                                    }
                                }]
                            }
                        }}
                        series={[{
                            name: "",
                            data: newData.map((d) => (d.score).toFixed(2))
                        }]}
                    />
                </div>

            </RenderComponent>
        </div>
    );
}

export default WhiteSpaceColumnChart;
